<template>
  <div class="fts-script-editor" :class="[
    { 'script-editor--h-full': fullHeight },
    { 'script-editor--disabled': disabled }
  ]">
    <div class="script-editor-container" :class="{ 'script-editor-container--expand': fullView }">
      <div class="script-editor__header" v-if="!disabledHeader">
        <div class="script-editor__header-group">
          <div class="script-editor-lang script-editor-lang--python" v-if="getLang === 'python'">Python</div>
          <div class="script-editor-lang script-editor-lang--html" v-else-if="getLang === 'html'">HTML</div>
          <div class="script-editor-lang script-editor-lang--javascript" v-else-if="getLang === 'javascript'">JavaScript
          </div>
          <div class="script-editor-lang script-editor-lang--json" v-else-if="getLang === 'json'">JSON</div>
          <div class="script-editor-lang script-editor-lang--css" v-else-if="getLang === 'css'">CSS</div>
          <div class="script-editor-lang script-editor-lang--css" v-else-if="getLang === 'liquid'">Liquid</div>
          <div class="script-editor__header-label">
            <slot name="label" :label="label">
              {{ label }}
            </slot>
          </div>
        </div>
        <div class="script-editor__header-action">
          <!-- D17: the script editor's isolated GNey mode — drafts land in THIS buffer,
               reviewed here (with the editor's test/debug tools), saved by the host modal. -->
          <fts-btn sm text :color="ai.open ? 'primary' : 'default'" text-transform-init
            v-if="gneyScriptEnabled" @click="ai.open = !ai.open">
            GNey <fts-release-badge alpha class="fts-align-top" />
          </fts-btn>
          <fts-tippy content="Exit full screen mode" key="window-restore" v-if="fullView">
            <fts-btn sm text icon @click="toggleView()">
              <font-awesome-icon :icon="['far', 'window-restore']" />
            </fts-btn>
          </fts-tippy>

          <fts-tippy content="Expand to full screen" key="window-maximize" v-else>
            <fts-btn sm text icon @click="toggleView(true)">
              <font-awesome-icon :icon="['far', 'window-maximize']" />
            </fts-btn>
          </fts-tippy>

          <fts-btn sm text color="secondary" :loading="isTesting" @click="$emit('test')" v-if="fullView && testButton">
            <font-awesome-icon :icon="['fas', 'play']" /> Test
          </fts-btn>

          <fts-btn sm text color="primary" :loading="isSaving" @click="$emit('save')" v-if="fullView && saveButton">
            <font-awesome-icon :icon="['fas', 'check']" /> Save
          </fts-btn>
        </div>
      </div>

      <!-- GNey script bar (D17): brief in, complete-script draft out, replaced in the
           buffer with a one-click undo. Nothing saves here — the host modal does. -->
      <div class="script-editor-gney" v-if="gneyScriptEnabled && ai.open">
        <div class="script-editor-gney__row">
          <input class="fts-form-control script-editor-gney__input"
            v-model="ai.brief" :disabled="ai.busy"
            :placeholder="content && content.trim() ? 'Describe the change to this script…' : 'Describe the script to write…'"
            @keydown.enter.prevent="generateScriptDraft" />
          <fts-btn sm color="primary" text-transform-init
            :loading="ai.busy" :disabled="ai.busy || !ai.brief.trim()"
            @click="generateScriptDraft">Draft</fts-btn>
          <fts-btn sm text text-transform-init v-if="ai.prior != null"
            @click="undoScriptDraft">Undo draft</fts-btn>
        </div>
        <div class="script-editor-gney__notes" v-if="ai.notes">{{ ai.notes }}</div>
        <div class="script-editor-gney__error" v-if="ai.error">{{ ai.error }}</div>
      </div>

      <div class="script-editor__content">
        <div class="script-editor-nav-l" :class="{ 'script-editor-nav-l--collapse': dictionaryCollapse }"
          v-if="!disabledDictionary">
          <div class="script-editor-nav-l__header" v-if="dictionaryCollapse">
            <div class="script-editor-module-select-wrapper">
              <fts-tippy append-trigger-class="script-editor-nav-l-toggle" content="Open dictionary"
                @trigger:click="dictionaryCollapse = false">
                <font-awesome-icon :icon="['fas', 'angles-left']" rotation="180" />
              </fts-tippy>
            </div>
          </div>

          <template v-else>
            <div class="script-editor-nav-l__header">
              <div class="script-editor-module-select-wrapper">
                <fts-tippy append-trigger-class="script-editor-module-redirect-back"
                  :content="`Back to ${module.name || 'current module'}`" @trigger:click="setDictionary(module)"
                  v-if="module.id != dictionaryModule.id">
                  <font-awesome-icon :icon="['fas', 'angle-left']" />
                </fts-tippy>

                <fts-dropdown-select append-to-body prevent-overflow-alt-axis prevent-overflow-tether-off
                  content-class="dropdown-menu dropdown-menu-w-sm" overlay class="script-editor-module-select">
                  <template #toggle>
                    <fts-label-group :item="dictionaryModule" emoji :emoji-size="14" />
                  </template>
                  <template #menu="{ select }">
                    <div v-for="x in modules" :key="x.id" class="dropdown-option" @click="select(); setDictionary(x)">
                      <fts-label-group :item="x" emoji :emoji-size="14" />
                    </div>
                  </template>
                </fts-dropdown-select>

                <fts-tippy append-trigger-class="script-editor-nav-l-toggle" content="Close dictionary"
                  @trigger:click="dictionaryCollapse = true">
                  <font-awesome-icon :icon="['fas', 'angles-left']" />
                </fts-tippy>
              </div>

              <div class="fts-form-control-filter script-editor-filter">
                <input 
                  class="fts-form-control" 
                  v-model="dictionaryKeyword" 
                  placeholder="Search..." 
                  autocomplete="script-editor-filter" 
                  @keydown.esc.prevent="dictionaryKeyword = null" 
                />
                <div class="times-icon" @click="dictionaryKeyword = null;" v-if="dictionaryKeyword">
                  <font-awesome-icon :icon="['fas', 'xmark']" fixed-width />
                </div>
                <div class="search-icon" v-else>
                  <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                </div>
              </div>
            </div>

            <div class="script-editor-nav-l__content fts-scrollbar" ref="dictionary-content">
              <div class="script-editor-loading-overlay" v-if="dictionaryLoading">
                <fts-spinner show :size="16" />
              </div>

              <template v-else-if="getInformation && getInformation.length">
                <template v-for="x in getInformation">
                  <div :key="x.id" class="script-editor-nav-item" @click="evt => pasteValue(evt, x)">
                    <fts-label-group :item="x" information />
                    <div class="stopPropagation script-editor-nav-item-action"
                      @click="redirectModuleByConnectionId(x.connectionId)" v-if="
                        x.connectionId &&
                        x.connectionId != module.id &&
                        (
                          x.type == 7 ||
                          x.type == 8 ||
                          x.type == 20 ||
                          x.type == 21
                        )
                      ">
                      <font-awesome-icon :icon="['fas', 'external-link-alt']" fixed-width />
                    </div>
                  </div>
                  <template v-if="x.type == 14 && x.fields && x.fields.length">
                    <div v-for="field in x.fields" :key="field.id"
                      class="script-editor-nav-item script-editor-nav-item--child"
                      @click="evt => pasteValue(evt, field)">
                      <fts-label-group :item="field" information />
                      <div class="stopPropagation script-editor-nav-item-action"
                        @click="redirectModuleByConnectionId(field.connectionId)" v-if="
                          field.type == 7 ||
                          field.type == 8 ||
                          field.type == 20 ||
                          field.type == 21
                        ">
                        <font-awesome-icon :icon="['fas', 'external-link-alt']" fixed-width />
                      </div>
                    </div>
                  </template>
                </template>
              </template>

              <div class="script-editor-filter-message" v-else-if="dictionaryKeyword">
                <div class="script-editor-filter-message__title">No result found</div>
                <div class="script-editor-filter-message__description">Try different search term</div>
              </div>

              <div class="script-editor-filter-message" v-else>
                <div class="script-editor-filter-message__title">No result found</div>
              </div>
            </div>
          </template>
        </div>
        <div class="script-editor__content-main">
          <div class="script-editor-wrapper" :id="getEditorId" />
          <div class="script-editor-bar" v-if="fullView"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import commons from '../commons/commons';
import { store as canvasDesignStore } from '../store/canvasDesignStore';

import FTSBtn from '../elements/FTSBtn';
import FTSDropdownSelect from '../elements/FTSDropdownSelect';
import FTSLabelGroup from '../elements/FTSLabelGroup';
import FTSSpinner from '../elements/FTSSpinner';
import FTSTippy from '../elements/FTSTippy';
import FTSReleaseBadge from '../elements/FTSReleaseBadge';
import { commonStore } from '../store/commonStore';

export default {
  components: {
    "fts-btn": FTSBtn,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-label-group": FTSLabelGroup,
    "fts-spinner": FTSSpinner,
    "fts-tippy": FTSTippy,
    "fts-release-badge": FTSReleaseBadge
  },
  props: {
    editorId: String,
    content: {
      default: ""
    },
    lang: {
      type: String,
      default: 'html'
    },
    label: String,
    fullHeight: Boolean,
    disabledHeader: Boolean,
    disabledDictionary: Boolean,
    disabled: Boolean,
    testButton: Boolean,
    isTesting: Boolean,
    saveButton: Boolean,
    isSaving: Boolean
  },
  model: {
    prop: 'content',
    event: "update-content",
  },
  data: () => ({
    uid: null,
    editor: null,
    beforeContent: "",
    fullView: false,
    dictionaryCollapse: false,
    dictionaryLoading: false,
    dictionaryKeyword: null,
    dictionaryModule: {},
    dictionaryInformation: null,
    /* D17 — the isolated GNey script mode's state; prior holds the pre-draft content for
       the one-click undo. */
    ai: { open: false, brief: "", busy: false, error: "", notes: "", prior: null }
  }),
  methods: {
    /* ---- D17: the script editor's isolated GNey mode ---------------------------------- */
    async generateScriptDraft() {
      const brief = this.ai.brief.trim();
      if (!brief || this.ai.busy) return;
      this.ai.busy = true;
      this.ai.error = "";
      this.ai.notes = "";
      try {
        const wsId = canvasDesignStore.getters.workspaceId;
        const res = await commonStore.state.client.post("/api/teammate/studio/script", {
          ModuleId: canvasDesignStore.state.vectorId,
          Label: this.label,
          Lang: this.getLang,
          Script: this.content || "",
          Brief: brief,
        }, wsId ? { headers: { wsId } } : {});
        const draft = res.data || {};
        if (!draft.success || !draft.script) {
          this.ai.error = draft.message || "GNey could not draft this script.";
          return;
        }
        this.ai.prior = this.content || "";
        this.$emit("update-content", draft.script);
        this.ai.notes = draft.notes || "Draft placed in the editor — review it, test it, then save.";
        this.ai.brief = "";
      } catch (err) {
        const data = err && err.response && err.response.data;
        this.ai.error = (data && data.message) || "GNey could not draft this script.";
      } finally {
        this.ai.busy = false;
      }
    },
    undoScriptDraft() {
      if (this.ai.prior == null) return;
      this.$emit("update-content", this.ai.prior);
      this.ai.prior = null;
      this.ai.notes = "";
    },
    /* D17 chip: announce this editor's script to the studio GNey panel. Focus claims the
       chip (`claim`); content changes refresh it only while this editor holds it (the
       panel keys on `key`). Debounced — scripts change per keystroke. */
    broadcastScriptContext(claim) {
      if (!this.gneyScriptEnabled || !window.eventBus) return;
      if (claim) this._scriptCtxClaimed = true;
      if (!this._scriptCtxClaimed) return;
      clearTimeout(this._scriptCtxTimer);
      this._scriptCtxTimer = setTimeout(() => {
        window.eventBus.$emit("teammate:scriptContext", {
          key: this.getEditorId,
          label: this.label || this.getEditorId,
          lang: this.getLang,
          script: this.content || "",
        });
      }, claim ? 0 : 400);
    },
    toggleView(fullView = false) {
      this.fullView = fullView;
      this.$emit("fullview-change", fullView);
      this.$nextTick(() => {
        this.editor.resize();
      });
    },
    pasteValue(event, item) {
      if (!commons.elementStopPropagation(event) && item) {
        if (item.system && !item.userCollection) {
          this.editor.execCommand("paste", `.${item.fieldName}`);
        } else {
          this.editor.execCommand("paste", `['${item.displayName}']`);
        }
        this.editor.focus();
      }
    },
    async setDictionary(module) {
      try {
        this.dictionaryLoading = true;
        this.dictionaryModule = module;
        this.dictionaryKeyword = null;
        this.dictionaryInformation = [
          {
            id: "id",
            fieldName: "Id",
            displayName: "ID",
            type: 37,
            system: true
          },
          {
            id: "documentId",
            fieldName: "DocumentId",
            displayName: "Document ID",
            type: 0,
            system: true
          },
          {
            id: "createdBy",
            fieldName: "CreatedBy",
            displayName: "Created By",
            type: 19,
            system: true
          },
          {
            id: "createdDate",
            fieldName: "CreatedDate",
            displayName: "Created Date",
            type: 5,
            system: true
          },
          {
            id: "lastChangeBy",
            fieldName: "UpdatedBy",
            displayName: "Last Change By",
            type: 19,
            system: true
          },
          {
            id: "lastChangeDate",
            fieldName: "UpdatedDate",
            displayName: "Last Change Date",
            type: 5,
            system: true
          },
          {
            id: "state",
            fieldName: "State",
            displayName: "State",
            type: 0,
            system: true
          }
        ];
        const res = await canvasDesignStore.dispatch("findVariable", { moduleId: module.id });
        if (res && res.isUserCollection) {
          this.dictionaryInformation.push({
            id: "username",
            displayName: "Username",
            type: 19,
            system: true,
            userCollection: true
          });
        }
        if (res && res.information) {
          this.dictionaryInformation = [...res.information, ...this.dictionaryInformation];
        }
      } catch (err) {
        console.error(err);
        this.$toasted.global.error({
          response: err.response,
          message: "Failed to retrieve module information."
        });
      } finally {
        this.dictionaryLoading = false;
      }
    },
    redirectModuleByConnectionId(connectionId) {
      const findModule = this.modules.find((x) => x.id == connectionId);
      if (findModule) {
        this.setDictionary(findModule);
      } else {
        this.$toasted.info("Module is unavailable or has been removed.");
      }
    }
  },
  computed: {
    module() {
      return canvasDesignStore.state.vector;
    },
    modules() {
      return canvasDesignStore.getters.workspaceModules;
    },
    getInformation() {
      const deepFilterByKeyword = (list, keyword) =>
        list.reduce((accumulator, item) => {
          if (item && item.displayName.toLowerCase().includes(keyword.toLowerCase())) {
            accumulator.push(item);
          } else if (item.type == 14 && Array.isArray(item.fields)) {
            const filteredChildren = deepFilterByKeyword(item.fields, keyword);
            if (filteredChildren.length > 0) {
              accumulator.push({ ...item, fields: filteredChildren });
            }
          }
          return accumulator;
        }, []);
      return this.dictionaryKeyword && Array.isArray(this.dictionaryInformation) ?
        deepFilterByKeyword(this.dictionaryInformation, this.dictionaryKeyword) :
        this.dictionaryInformation;
    },
    getEditorId() {
      return this.editorId || this.uid;
    },
    getLang() {
      return (this.lang || "html").toLowerCase();
    },
    /* D17: the GNey script mode shows only where it belongs — script languages, an open
       Studio draft (vectorId), the alpha gate on, and an editable editor. */
    gneyScriptEnabled() {
      return !this.disabled
        && ["python", "javascript"].indexOf(this.getLang) >= 0
        && !!canvasDesignStore.state.vectorId
        && !!canvasDesignStore.getters.canvasOpsEnabled;
    }
  },
  watch: {
    content(val) {
      if (this.beforeContent !== val) {
        this.editor && this.editor.setValue(val || "", 1);
      }
    },
  },
  beforeDestroy() {
    // D17 chip: the host modal closed — release the panel's script chip if it is ours.
    clearTimeout(this._scriptCtxTimer);
    if (this._scriptCtxClaimed && window.eventBus)
      window.eventBus.$emit("teammate:scriptContext", { key: this.getEditorId, closed: true });
  },
  mounted() {
    !Boolean(!this.editorId) && (this.uid = `script-editor-${this._uid}`);
    this.$nextTick(() => {
      this.editor = window.ace.edit(this.getEditorId);
      this.editor.setValue(this.content || "", 1);
      this.editor.session.setMode(`ace/mode/${this.getLang}`);
      this.editor.setOptions({
        autoScrollEditorIntoView: true,
        // minLines: 10,
        // maxLines: 300
      });

      if (this.disabled) {
        this.editor.setReadOnly(true);
      } else {
        this.editor.on("change", () => {
          var val = this.editor.getValue();
          this.beforeContent = val;
          this.$emit("update-content", val);
          //this.debouncedEmit(val);
          this.broadcastScriptContext();
        });
        // D17 chip: the focused editor is "the open script" the studio GNey panel chips —
        // the client-surface pattern (form/entry context broadcasts), applied to scripts.
        this.editor.on("focus", () => this.broadcastScriptContext(true));
      }

      if (!this.disabledDictionary) {
        this.setDictionary({
          id: this.module.id,
          emoji: this.module.emoji,
          name: this.module.name
        });
      }
    })
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

/* D17: the GNey script bar */
.script-editor-gney {
  padding: .375rem .5rem;
  border-bottom: 1px solid var(--fts-gray-200);
  .script-editor-gney__row {
    display: flex;
    align-items: center;
    gap: .375rem;
  }
  .script-editor-gney__input {
    flex: 1;
    height: 1.875rem;
    font-size: .8125rem;
  }
  .script-editor-gney__notes {
    margin-top: .25rem;
    font-size: .75rem;
    opacity: .8;
  }
  .script-editor-gney__error {
    margin-top: .25rem;
    font-size: .75rem;
    color: var(--fts-danger);
  }
}

.fts-script-editor {
  min-height: 350px;
  max-height: 350px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .script-editor-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &.script-editor-container--expand {
      position: fixed;
      inset: 0;
      background-color: var(--fts-card-background);
      z-index: $z-index-fixed;

      .script-editor__header {
        margin-bottom: 0;
        padding: 0.375rem 0.75rem;
      }

      .script-editor__content {
        max-height: 100%;
        overflow: hidden;
      }

      .script-editor-nav-l:not(.script-editor-nav-l--collapse) {
        width: 300px;
      }
    }
  }

  .script-editor__header {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.25rem;
  }

  .script-editor__content {
    min-height: 0;
    flex: 1;
    display: flex;
    flex-wrap: nowrap;
    border: 1px solid var(--fts-border);
  }

  .script-editor__header-group {
    display: flex;
    align-items: center;
  }

  .script-editor__header-label {
    font-size: $font-size;
    line-height: $line-height;
    color: var(--fts-form-label);
  }

  .script-editor__header-action {
    display: flex;
    align-items: center;

    >*:not(:first-child) {
      margin-left: 0.25rem;
    }
  }

  .script-editor__content-main {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .script-editor-nav-l {
    display: flex;
    flex-direction: column;
    border-right: 1px solid var(--fts-border);

    &:not(.script-editor-nav-l--collapse) {
      width: 200px;
    }
  }

  .script-editor-nav-l-toggle {
    font-size: 0.75rem;
    color: var(--fts-default);
    cursor: pointer;
  }

  .script-editor-nav-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-left: 0.25rem;
    margin-right: 0.25rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    border-radius: 0.25rem;
    font-size: 13px;
    cursor: pointer;

    .fts-label-group {
      padding-top: 0.375rem;
      padding-bottom: 0.375rem;
    }

    &.script-editor-nav-item--child {
      padding-left: 1.675rem;
    }

    &:hover {
      background-color: rgba(var(--fts-gray-100-rgb), 0.75);

      .script-editor-nav-item-action {
        display: block;
      }
    }
  }

  .script-editor-nav-item-action {
    display: none;
    padding: 0.125rem;
    border-radius: 0.25rem;
    color: var(--fts-default);

    &:hover {
      box-shadow: var(--fts-elevation-1);
      background-color: var(--fts-gray-200);
      color: var(--fts-text);
    }
  }

  .script-editor-wrapper {
    flex: 1;
  }

  .script-editor-lang {
    margin-right: 0.375rem;
    padding-left: 0.15rem;
    padding-right: 0.15rem;
    border-radius: 0.125rem;
    font-size: 0.675rem;
    line-height: 1rem;
    letter-spacing: 0.025rem;

    &.script-editor-lang--python {
      background-color: rgb(52, 115, 164);
      color: rgb(255, 211, 64);
    }

    &.script-editor-lang--html {
      background-color: rgb(228, 77, 38);
      color: rgb(255 255 255);
    }

    &.script-editor-lang--javascript {
      background-color: rgb(247, 223, 30);
      color: #212121;
    }

    &.script-editor-lang--json {
      background-color: rgb(247, 223, 30);
      color: #212121;
    }

    &.script-editor-lang--css {
      background-color: rgb(33 76 229);
      color: rgb(255 255 255);
    }
  }

  .script-editor-bar {
    height: 2.5rem;
    border-top: 1px solid var(--fts-border);
  }

  .script-editor-nav-l__header {
    padding: 0.5rem;
  }

  .script-editor-nav-l__content {
    flex: 1;
    padding-bottom: 0.5rem;
  }

  .script-editor-module-select-wrapper {
    min-width: 0;
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .script-editor-module-redirect-back {
    font-size: 0.75rem;
    color: var(--fts-default);
    cursor: pointer;
  }

  .script-editor-module-select {
    min-width: 0;
    flex: 1;

    .fts-label-group {
      font-size: 0.75rem;
    }

    &:not(:first-child) {
      margin-left: 0.5rem;
    }

    &:not(:last-child) {
      margin-right: 0.5rem;
    }
  }

  .script-editor-filter {
    margin-left: -0.125rem;
    margin-right: -0.125rem;

    >.fts-form-control {
      border-radius: 0.25rem;
      font-size: 0.75rem;
      line-height: 1rem;
    }
  }

  .script-editor-filter-message {
    padding: 0.75rem;
    text-align: center;
    font-size: 0.75rem;
    line-height: 1rem;
  }

  .script-editor-filter-message__title {
    font-weight: 500;
    color: var(--fts-text);
  }

  .script-editor-filter-message__description {
    color: var(--fts-default);

    &:not(:last-child) {
      margin-top: 0.25rem;
    }
  }

  .script-editor-loading-overlay {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.script-editor--h-full {
    min-height: 100%;
    max-height: 100%;
  }
  &.script-editor--disabled {
    .script-editor-wrapper {
      position: relative;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: var(--fts-disabled);
        cursor: not-allowed;
        z-index: 10;
      }

      .ace_cursor {
        display: none;
      }
    }
  }
}
</style>